import { Directive, HostListener, ElementRef } from '@angular/core';
import { ToastService } from '../services/toast.service';
import { NgControl } from '@angular/forms';
import { LanguageService } from '../services/language.service';

@Directive({
  selector: '[descriptionRestriction]'
})
export class DescriptionRestrictionDirective {

  // Allowed characters regex: allow letters, numbers, and specific special characters
  private allowedCharacters: RegExp = /^[a-zA-Z0-9\u0900-\u097F-_., \t\n\r]*$/;

  // Regex to disallow only numbers or only spaces
  private noOnlyNumbersOrSpaces: RegExp = /^(?![0-9\s]+$).*/;

  // Regular expression to detect emojis (using Unicode ranges for common emojis)
  private emojiRegex: RegExp = /[\u{1F600}-\u{1F64F}|\u{1F300}-\u{1F5FF}|\u{1F680}-\u{1F6FF}|\u{1F700}-\u{1F77F}|\u{1F780}-\u{1F7FF}|\u{1F800}-\u{1F8FF}|\u{1F900}-\u{1F9FF}|\u{1FA00}-\u{1FA6F}|\u{1FA70}-\u{1FAFF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}|\u{2B50}-\u{2B55}|\u{23F0}-\u{23F3}|\u{2B06}-\u{2B07}|\u{2B05}-\u{2B07}]/u;

  private maxLength: number = 500;
  activeLanguage: any;

  constructor(private el: ElementRef, private control: NgControl, private toasterService: ToastService, private langService: LanguageService,) { }

  // Keypress handler to restrict input to allowed characters, prevent emojis, and enforce max length
  @HostListener('keypress', ['$event']) onKeyPress(event: KeyboardEvent) {
    const char = event.key;
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentText = inputElement.value;

    // Check if the input exceeds the max length
    if (currentText.length >= this.maxLength) {
      event.preventDefault();
      this.toasterService.show('Maximum character limit of 500 reached.', {
        class: 'danger',
        title: 'Input Limit Reached'
      });
      return;
    }

    // Validate allowed characters and disallow emojis
    if (!this.allowedCharacters.test(char) || this.emojiRegex.test(char)) {
      event.preventDefault();
      this.listenToLanguageChanges();
      if(this.activeLanguage == 'mr'){
        this.toasterService.show(`अवैध वर्ण "${char}"! फक्त पूर्णविराम (.), स्वल्पविराम (,), हायफन (-), आणि अंडरस्कोर (_) विशेष वर्णांना अनुमती आहे. इमोजींना परवानगी नाही.`, {
          class: 'danger',
          title: 'Invalid Input'
        });
      }
      else{
        this.toasterService.show(`Invalid character "${char}"! Only period (.), comma (,), hyphen (-), and underscore (_) special characters are allowed. Emojis are not allowed.`, {
          class: 'danger',
          title: 'Invalid Input'
        });
      } 
    }
  }

  // Input handler to sanitize the field from any emoji or invalid characters after typing or pasting
  @HostListener('input', ['$event']) onInput(event: Event) {
    const inputEvent = event as InputEvent;
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const value = inputElement.value;

    if (inputEvent.inputType === 'deleteContentBackward' || inputEvent.inputType === 'deleteContentForward') {
      return;
    }

    // Remove invalid characters and emojis from the input field
    const sanitizedValue = value.replace(/[^a-zA-Z0-9\u0900-\u097F-_., \t\n\r]/g, '').replace(this.emojiRegex, '');

    // Check if input contains only numbers or spaces
    if (!this.noOnlyNumbersOrSpaces.test(sanitizedValue)) {
      // Show error if only numbers or spaces
      this.control.control?.setErrors({ pattern: true });
    } else {
      // Clear the error if valid
      this.control.control?.setErrors(null);
    }

    // Update the input element value if it was sanitized
    if (sanitizedValue !== value) {
      inputElement.value = sanitizedValue;
    }
  }

  // Paste handler to restrict input to allowed characters and prevent pasting emojis
  @HostListener('paste', ['$event']) onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentText = inputElement.value;

    // Check if the pasted content exceeds the max length
    if (currentText.length + pastedText.length > this.maxLength) {
      event.preventDefault();
      this.toasterService.show('Pasting this text will exceed the maximum character limit of 500.', {
        class: 'danger',
        title: 'Input Limit Reached'
      });
      return;
    }

    // Validate allowed characters and disallow emojis in the pasted content
    if (!this.allowedCharacters.test(pastedText) || this.emojiRegex.test(pastedText)) {
      event.preventDefault();
      this.toasterService.show('Pasting invalid characters is not allowed! Emojis and certain characters are disallowed.', {
        class: 'danger',
        title: 'Invalid Paste'
      });
    }
  }
  private listenToLanguageChanges() {
    this.langService.language$.subscribe((language: any) => {
      this.activeLanguage = language;
    });
  }
}
